import React, { useCallback } from 'react';
import _get from 'lodash/get';
import { Button, Tooltip } from 'grab-ui';
import I18n from 'i18n-js';
import optimizeRouteIcon from 'public/icons/optimize-route.svg';
import circledCheckMark from 'public/icons/circled-check-mark.svg';
import cx from 'classnames';
import PropTypes from 'prop-types';
import styles from './route-optimization-button.less';
import {
  useLazyLoadLottie,
  ANIMATION_KEYS,
} from '../../../../hooks/useLazyLoadLottie';

const CommonDisabledButton = ({ text }) => (
  <Button className={cx(styles.roButton, styles.disabled)} disabled>
    {text && (
      <span className={styles.icon}>
        <img src={optimizeRouteIcon} alt={''} />
      </span>
    )}
    {text}
  </Button>
);

CommonDisabledButton.defaultProps = {
  text: undefined,
};

CommonDisabledButton.propTypes = {
  text: PropTypes.string,
};

const RouteOptimizationButton = ({
  delivery,
  onEnableRouteOptimization,
  onRevertRouteOptimization,
}) => {
  const fetchingQuote = _get(delivery, 'fetchingQuote');
  const routeOptimization = _get(delivery, 'routeOptimization');
  const pickUpStep = _get(delivery, 'detail.pickUp');
  const dropOffSteps = _get(delivery, 'detail.dropOffs');
  const preventAutoApplyRO = _get(routeOptimization, 'preventAutoApplyRO');
  const bestRoute = _get(
    delivery,
    'quote.alternativeFare[0].routeOptimization',
  );
  const optimizedStepsResult = _get(bestRoute, 'OptimizedSteps');
  // To check applied state
  const OptimizedSteps = _get(routeOptimization, 'OptimizedSteps');
  const enabledRO = _get(routeOptimization, 'enabled');

  const playLottieAnimOnce = useCallback(anim => {
    if (!anim) {
      return;
    }
    if (!window._roRottieAnimPlayed) {
      setTimeout(() => {
        window._roRottieAnimPlayed = true;
      }, 1000);
    }
  }, []);

  const {
    LottieComponent: ROAnimationData,
    isLoading: isROLoading,
  } = useLazyLoadLottie(ANIMATION_KEYS.ROUTE_OPTIMISATION, {
    ref: c => playLottieAnimOnce(c),
    options: {
      loop: false,
      autoplay: false,
    },
    height: 18,
    width: 18,
  });

  if (!_get(pickUpStep, 'poi') || dropOffSteps.filter(s => s.poi).length < 2) {
    return '';
  }
  if (fetchingQuote) {
    return (
      <CommonDisabledButton text={I18n.t('v2.locationDrawer.updatingRoute')} />
    );
  }

  // 1. show Find the shortest route:
  // - There is preset and have RO but not apply yet
  // - There is revert case
  if (
    !enabledRO ||
    (enabledRO &&
      preventAutoApplyRO &&
      !!optimizedStepsResult &&
      !OptimizedSteps)
  ) {
    return (
      <Tooltip
        placement="bottomRight"
        title={I18n.t('v2.locationDrawer.tooltip.findShortestRoute')}
      >
        <Button className={styles.roButton} onClick={onEnableRouteOptimization}>
          <span className={styles.icon}>
            {!window._roRottieAnimPlayed ? (
              !isROLoading && ROAnimationData && <ROAnimationData />
            ) : (
              <img src={optimizeRouteIcon} alt={''} />
            )}
          </span>
          {I18n.t('v2.locationDrawer.findShortestRoute')}
        </Button>
      </Tooltip>
    );
  }

  // 2. There is RO and not have any optimized steps
  if (enabledRO && !OptimizedSteps) {
    return (
      <Tooltip
        placement="bottomRight"
        title={I18n.t('v2.locationDrawer.tooltip.alreadyShortened')}
      >
        <Button className={cx(styles.roButton, styles.disabled)}>
          <span className={styles.icon}>
            <img src={circledCheckMark} alt={''} />
          </span>
          {I18n.t('v2.locationDrawer.routeAlreadyShortened')}
        </Button>
      </Tooltip>
    );
  }

  // 3. Remain case, show Revert button:
  // Preset, has RO, and then click Apply
  // No preset, has RO, and auto apply
  return (
    <Tooltip
      placement="bottomRight"
      title={I18n.t('v2.locationDrawer.tooltip.useOriginalRoute')}
    >
      <Button className={styles.roButton} onClick={onRevertRouteOptimization}>
        <span className={styles.icon}>
          <img src={optimizeRouteIcon} alt={''} />
        </span>
        {I18n.t('v2.locationDrawer.useOriginalRoute')}
      </Button>
    </Tooltip>
  );
};

RouteOptimizationButton.propTypes = {
  delivery: PropTypes.shape({}).isRequired,
  onEnableRouteOptimization: PropTypes.func.isRequired,
  onRevertRouteOptimization: PropTypes.func.isRequired,
};

export default RouteOptimizationButton;
